import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';

/**
 * Error messages for evaluation modal failures.
 * These provide user-friendly messages for common error scenarios.
 */
export const EVALUATION_MODAL_ERRORS = {
    NO_COHORT: 'Cannot open evaluation: this submission has no associated cohort.',
    FETCH_FAILED: 'Failed to load evaluation data. Please try again.',
    MODAL_FAILED: 'Failed to open the evaluation modal. Please try again.',
} as const;

/**
 * Type guard to check if an error is an RTK Query FetchBaseQueryError.
 */
function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
    return typeof error === 'object' && error !== null && 'data' in error;
}

/**
 * Type guard to check if error data contains a message property.
 */
function hasMessage(data: unknown): data is { message: string } {
    return (
        typeof data === 'object' &&
        data !== null &&
        'message' in data &&
        typeof (data as { message: unknown }).message === 'string'
    );
}

/**
 * Extracts a user-friendly error message from an RTK Query error or unknown error.
 *
 * The API returns errors in the format { message: "error text" }, so we check
 * for that structure first. Falls back to a generic message if the error format
 * is unexpected.
 *
 * @param error - The error object (typically from RTK Query)
 * @param fallbackMessage - Message to use if error format is unrecognized
 * @returns A user-friendly error message string
 */
export function getErrorMessage(
    error: unknown,
    fallbackMessage: string = EVALUATION_MODAL_ERRORS.FETCH_FAILED,
): string {
    if (isFetchBaseQueryError(error) && hasMessage(error.data)) {
        return error.data.message;
    }

    // Handle standard Error objects
    if (error instanceof Error && error.message) {
        return error.message;
    }

    return fallbackMessage;
}
